import Button from '@rio-cloud/rio-uikit/Button';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { TagCreationDialog } from '../../create/TagCreationDialog';
import { openTagCreationDialog } from '../../create/tagCreationDialogSlice';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import type { TagTableColumnConfig } from '../TagList';
import {
    getSearchString,
    getTableViewType,
    tagsTableSearchChanged,
    tagsTableViewTypeChanged,
} from './tagListToolbarSlice';
import { TagTableDisplayOptionsDropdown } from './TagTableDisplayOptionsDropdown';

type Props = {
    showAddButton?: boolean;
    columns: TagTableColumnConfig[];
    compactRows: boolean;
    gridLines: boolean;
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    onColumnsReset: () => void;
    onToggleCompactRows: () => void;
    onToggleGridLines: () => void;
};

export const TagListToolbar = ({
    showAddButton = true,
    columns,
    compactRows,
    gridLines,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnsReset,
    onToggleCompactRows,
    onToggleGridLines,
}: Props) => {
    const dispatch = useAppDispatch();
    const tableViewType = useAppSelector(getTableViewType);
    const searchString = useAppSelector(getSearchString);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const hasSyncedSearchFromUrlRef = useRef(false);
    const hasSyncedDisplayOptionsFromUrlRef = useRef(false);
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const searchValue = searchParams.get('q') ?? searchParams.get('search') ?? '';

        if (!hasSyncedSearchFromUrlRef.current) {
            hasSyncedSearchFromUrlRef.current = true;

            if (searchValue !== searchString) {
                dispatch(tagsTableSearchChanged(searchValue));
            }

            return;
        }

        if (searchValue !== searchString) {
            dispatch(tagsTableSearchChanged(searchValue));
        }
    }, [dispatch, location.search, searchString]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const gridLinesFromUrl = searchParams.get('gridLines') === 'true';
        const compactRowsFromUrl = searchParams.get('compactRows') === 'true';

        if (!hasSyncedDisplayOptionsFromUrlRef.current) {
            hasSyncedDisplayOptionsFromUrlRef.current = true;
        }

        if (gridLinesFromUrl !== gridLines) {
            onToggleGridLines();
        }

        if (compactRowsFromUrl !== compactRows) {
            onToggleCompactRows();
        }
    }, [compactRows, gridLines, location.search, onToggleCompactRows, onToggleGridLines]);

    const handleOpenTagCreationDialog = () => {
        dispatch(openTagCreationDialog());
    };

    const handleViewTypeChange = (viewType: TableViewTogglesViewType) => dispatch(tagsTableViewTypeChanged(viewType));
    const handleSearchChange = (searchValue: string) => {
        dispatch(tagsTableSearchChanged(searchValue));

        const searchParams = new URLSearchParams(location.search);

        if (searchValue) {
            searchParams.set('q', searchValue);
            searchParams.delete('search');
        } else {
            searchParams.delete('q');
            searchParams.delete('search');
        }

        const nextSearch = searchParams.toString();
        const normalizedNextSearch = nextSearch ? `?${nextSearch}` : '';

        if (normalizedNextSearch === location.search) {
            return;
        }

        navigate(
            {
                pathname: location.pathname,
                search: normalizedNextSearch,
            },
            { replace: true }
        );
    };

    const updateDisplayOptionInUrl = (key: 'gridLines' | 'compactRows', enabled: boolean) => {
        const searchParams = new URLSearchParams(location.search);

        if (enabled) {
            searchParams.set(key, 'true');
        } else {
            searchParams.delete(key);
        }

        const nextSearch = searchParams.toString();
        const normalizedNextSearch = nextSearch ? `?${nextSearch}` : '';

        if (normalizedNextSearch === location.search) {
            return;
        }

        navigate(
            {
                pathname: location.pathname,
                search: normalizedNextSearch,
            },
            { replace: true }
        );
    };

    const handleToggleGridLines = () => {
        const nextGridLines = !gridLines;
        onToggleGridLines();
        updateDisplayOptionInUrl('gridLines', nextGridLines);
    };

    const handleToggleCompactRows = () => {
        const nextCompactRows = !compactRows;
        onToggleCompactRows();
        updateDisplayOptionInUrl('compactRows', nextCompactRows);
    };

    return (
        <div id='TagsTableToolbar'>
            <TableToolbar>
                {showAddButton && (
                    <TableToolbarColumn>
                        <Button
                            autoTrackingKey='tags.tagListToolbar.addButton'
                            bsStyle='primary'
                            iconName='plus'
                            id='addTag'
                            data-cy='add-tag'
                            onClick={handleOpenTagCreationDialog}
                        >
                            <FormattedMessage id='tags.tagListToolbar.addButton' />
                        </Button>
                    </TableToolbarColumn>
                )}
                <TableToolbarColumn horizontalAlign='right'>
                    <TagTableDisplayOptionsDropdown
                        compactRows={compactRows}
                        gridLines={gridLines}
                        intl={intl}
                        onToggleCompactRows={handleToggleCompactRows}
                        onToggleGridLines={handleToggleGridLines}
                    />
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right' separator className='min-width-200'>
                    <TableSearch
                        value={searchString}
                        inputRef={searchInputRef}
                        onChange={handleSearchChange}
                        placeholder={intl.formatMessage({
                            id: 'tags.tagListToolbar.searchPlaceholder',
                            defaultMessage: 'Search in the table',
                        })}
                        data-cy='tag-table-search-input'
                    />
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right'>
                    <TableViewToggles
                        onViewTypeChange={handleViewTypeChange}
                        viewType={tableViewType}
                        tableViewLabel={<FormattedMessage id='intl-msg:common-message.table.viewToggle.table' />}
                        singleCardViewLabel={<FormattedMessage id='intl-msg:common-message.table.viewToggle.list' />}
                        multiCardsViewLabel={<FormattedMessage id='intl-msg:common-message.table.viewToggle.cards' />}
                    />
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right'>
                    <TableColumnsDropdown
                        columns={columns.map(column => ({
                            key: column.key,
                            label: intl.formatMessage({
                                id: column.label,
                            }),
                            visible: column.visible,
                        }))}
                        label={
                            <FormattedMessage
                                id='intl-msg:common-message.table.columnsDropdown.columns'
                                defaultMessage='Columns'
                            />
                        }
                        onColumnOrderChange={onColumnOrderChange}
                        onColumnVisibilityChange={onColumnVisibilityChange}
                        onReset={onColumnsReset}
                        showResetButton
                        resetLabel={
                            <FormattedMessage
                                id='intl-msg:common-message.table.columnsDropdown.reset'
                                defaultMessage='Reset columns'
                            />
                        }
                    />
                </TableToolbarColumn>
            </TableToolbar>
            <TagCreationDialog />
        </div>
    );
};
